import { defineTheme } from "@astryxdesign/core/theme";
import { BODY_REFERENCE, onAccentColor } from "@shared/color.ts";
import { themes, defaultThemeSlug } from "@/astryx-themes";

const base = themes[defaultThemeSlug];

const cache = new Map<string, ReturnType<typeof defineTheme>>();

/**
 * The one runtime layer on top of the built neutral theme: the studio's accent
 * (from the database config), the salon look (warm ivory body, white cards,
 * soft Cyrillic serif headings) and the soft rounded Cyrillic body font. --color-on-accent
 * is set explicitly because, per Astryx docs, it stays baked from the base seed
 * when only --color-accent is overridden. The app renders in light mode only.
 */
export type HeadingFont = "serif" | "script" | "calligraphy" | "elegant";

const HEADINGS: Record<HeadingFont, { family: string; fallbacks: string; weight: "medium" | "normal" | "semibold" }> = {
  serif: { family: "Lora Variable", fallbacks: "Lora, Georgia, 'Times New Roman', serif", weight: "medium" },
  // Single-weight handwriting: a bolder weight would be synthesised and look smeared.
  script: { family: "Marck Script", fallbacks: "'Segoe Script', cursive", weight: "normal" },
  calligraphy: { family: "Great Vibes", fallbacks: "'Segoe Script', cursive", weight: "normal" },
  elegant: { family: "Cormorant Garamond Variable", fallbacks: "'Cormorant Garamond', Garamond, Georgia, serif", weight: "semibold" },
};

export function studioTheme(accent: string, headingFont: HeadingFont = "serif", accentText?: string) {
  const key = accent.toUpperCase();
  const cacheKey = `${key}|${headingFont}|${accentText ?? ""}`;
  const hit = cache.get(cacheKey);
  if (hit) return hit;
  // A brand may fix the text on its buttons (gold on black); validated for contrast in business.json.
  const onAccent = accentText?.toUpperCase() ?? onAccentColor(key);
  const theme = defineTheme({
    name: `studio-${key.slice(1).toLowerCase()}-${headingFont}${accentText ? `-${accentText.slice(1).toLowerCase()}` : ""}`,
    extends: base,
    tokens: {
      "--color-accent": [key, key],
      "--color-on-accent": [onAccent, onAccent],
      "--color-text-accent": [key, key],
      "--color-background-body": [BODY_REFERENCE, BODY_REFERENCE],
      "--color-background-muted": ["#F4EDE8", "#F4EDE8"],
      "--color-background-card": ["#FFFFFF", "#FFFFFF"],
      "--color-background-surface": ["#FFFFFF", "#FFFFFF"],
      "--color-background-popover": ["#FFFFFF", "#FFFFFF"],
    },
    // Fonts go through typography roles: the base theme's own roles would
    // otherwise win over plain --font-family-* token overrides.
    typography: {
      body: { family: "Nunito Variable", fallbacks: "Nunito, system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif" },
      heading: HEADINGS[headingFont],
    },
  });
  cache.set(cacheKey, theme);
  return theme;
}

/** Neutral accent used before a studio's config has loaded. */
export const LOADING_ACCENT = "#71717A";
